import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, addDays, differenceInDays } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  if (!date) {
    return '';
  }
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date in formatDate:', { date });
      return 'Invalid date';
    }
    return format(dateObj, "MMM d, yyyy");
  } catch (error) {
    console.error('Error formatting date:', error, { date });
    return 'Invalid date';
  }
}

export function formatDateTime(date: Date | string): string {
  if (!date) {
    return '';
  }
  
  try {
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date in formatDateTime:', { date });
      return 'Invalid date';
    }
    return format(dateObj, "MMM d, yyyy h:mm a");
  } catch (error) {
    console.error('Error formatting date time:', error, { date });
    return 'Invalid date';
  }
}

export function formatDay(dayIndex: number, startDate: Date | string): string {
  if (startDate === null || startDate === undefined) {
    return '';
  }
  
  try {
    const dateObj = startDate instanceof Date ? startDate : new Date(startDate);
    if (isNaN(dateObj.getTime())) {
      console.error('Invalid date in formatDay:', { startDate });
      return 'Invalid date';
    }
    
    // Calculate date for the specified day index
    const targetDate = new Date(dateObj);
    targetDate.setDate(dateObj.getDate() + dayIndex);
    
    return `Day ${dayIndex + 1} - ${format(targetDate, "EEE, MMM d")}`;
  } catch (error) {
    console.error('Error formatting day:', error, { dayIndex, startDate });
    return 'Invalid date';
  }
}

export function formatDateRange(dateRange: [Date, Date] | [string, string] | Date[] | string[]): string {
  if (!dateRange || !Array.isArray(dateRange) || dateRange.length < 2) {
    console.error('Invalid dateRange in formatDateRange:', { dateRange });
    return 'Invalid date range';
  }
  
  const [startDate, endDate] = dateRange;
  
  // Ensure startDate and endDate are Date objects
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  
  // Check for invalid dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    console.error('Invalid date in formatDateRange:', { startDate, endDate });
    return 'Invalid date range';
  }

  if (start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth()) {
    // Same month and year
    return `${format(start, "MMM d")} - ${format(end, "d, yyyy")}`;
  } else if (start.getFullYear() === end.getFullYear()) {
    // Same year, different month
    return `${format(start, "MMM d")} - ${format(end, "MMM d, yyyy")}`;
  } else {
    // Different years
    return `${format(start, "MMM d, yyyy")} - ${format(end, "MMM d, yyyy")}`;
  }
}

export function buildTripIntro(trip: {
  name: string;
  countries?: string[] | unknown;
  startDate?: string | Date | null;
  endDate?: string | Date | null;
}): string {
  const filteredCountries = Array.isArray(trip.countries)
    ? trip.countries.filter((c: unknown): c is string => typeof c === "string" && c.trim() !== "")
    : [];
  const countriesStr = filteredCountries.join(", ");
  const countries = countriesStr ? ` in ${countriesStr}` : "";
  const dates =
    trip.startDate && trip.endDate
      ? formatDateRange([new Date(trip.startDate), new Date(trip.endDate)])
      : "dates TBD";
  return `${trip.name}${countries} from ${dates}`;
}

export function getDaysBetweenDates(startDate: Date | string, endDate: Date | string): Date[] {
  // Ensure startDate and endDate are Date objects
  const start = startDate instanceof Date ? startDate : new Date(startDate);
  const end = endDate instanceof Date ? endDate : new Date(endDate);
  
  // Check for invalid dates
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    console.error('Invalid date in getDaysBetweenDates:', { startDate, endDate });
    return [];
  }
  
  const days: Date[] = [];
  const dayCount = differenceInDays(end, start) + 1;
  
  for (let i = 0; i < dayCount; i++) {
    days.push(addDays(new Date(start), i));
  }
  
  return days;
}